<template>
  <div class="range-explorer-tab p-4">
    <!-- Data Selection Controls -->
    <div class="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mb-4 sm:items-end min-w-0">
      <!-- Symbol Selector -->
      <div class="form-control w-full sm:max-w-xs">
        <label class="label">
          <span class="label-text">Symbol</span>
        </label>
        <div class="relative w-full">
          <input
            v-model="symbolInput"
            type="text"
            placeholder="Type or select symbol"
            class="input input-bordered w-full"
            @focus="showDropdown = true"
            @input="filterSymbols"
            @keydown.enter="selectSymbol(symbolInput)"
            @click.stop
          >
          <ul
            v-if="showDropdown && filteredSymbols.length > 0"
            class="absolute left-0 top-full mt-1 z-50 menu p-2 shadow-lg bg-base-100 rounded-box w-full max-h-60 overflow-y-auto border border-base-300"
            @click.stop
          >
            <li
              v-for="sym in filteredSymbols"
              :key="sym"
            >
              <a @click.stop="selectSymbol(sym)">{{ sym }}</a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Start Date/Time (GMT) -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Start (GMT)</span>
        </label>
        <input
          v-model="startDateTime"
          type="datetime-local"
          class="input input-bordered input-sm"
        >
      </div>

      <!-- End Date/Time (GMT) -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">End (GMT)</span>
        </label>
        <input
          v-model="endDateTime"
          type="datetime-local"
          class="input input-bordered input-sm"
        >
      </div>

      <!-- Timeframe Selector -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Timeframe</span>
        </label>
        <div class="join flex-wrap">
          <button
            v-for="tf in timeframes"
            :key="tf.value"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active': selectedTimeframe === tf.value }"
            @click="selectedTimeframe = tf.value"
          >
            {{ tf.label }}
          </button>
        </div>
      </div>

      <!-- Fetch Button -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">&nbsp;</span>
        </label>
        <button
          class="btn btn-primary w-full sm:w-auto"
          :disabled="isLoading || !symbol"
          @click="fetchOHLCData"
        >
          <span
            v-if="isLoading"
            class="loading loading-spinner loading-sm"
          />
          {{ isLoading ? 'Loading...' : 'Load Data' }}
        </button>
      </div>
    </div>

    <!-- Trading Mode Toggle -->
    <div class="mb-4 p-3 bg-base-200 rounded-lg">
      <div class="flex flex-wrap items-center gap-4">
        <span class="font-semibold text-sm">Trading Mode:</span>
        <div class="join flex-wrap">
          <button
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="tradingMode === 'LONG' ? 'btn-success' : 'btn-ghost'"
            @click="tradingMode = 'LONG'"
          >
            LONG
          </button>
          <button
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="tradingMode === 'SHORT' ? 'btn-error' : 'btn-ghost'"
            @click="tradingMode = 'SHORT'"
          >
            SHORT
          </button>
        </div>
        <span class="text-sm text-base-content/70">
          {{ tradingMode === 'LONG' ? `Grid: Current Price → ${selectedPeriodLabel} Low (buy dips)` : `Grid: Current Price → ${selectedPeriodLabel} High (sell rallies)` }}
        </span>
      </div>
    </div>

    <!-- Range Period Toggle -->
    <div class="mb-4 p-3 bg-base-200 rounded-lg">
      <div class="flex flex-wrap items-center gap-4">
        <span class="font-semibold text-sm">Range Period:</span>
        <div class="join flex-wrap">
          <button
            v-for="period in rangePeriods"
            :key="period.value"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active btn-primary': rangePeriod === period.value }"
            @click="rangePeriod = period.value"
          >
            {{ period.label }}
          </button>
        </div>
        <span class="text-sm text-base-content/70">
          Use {{ selectedPeriodLabel }} high/low for grid calculation
        </span>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="alert alert-error mb-4"
    >
      <span>{{ error }}</span>
    </div>

    <!-- Range Stats -->
    <div
      v-if="hasData"
      class="mb-4 stats shadow w-full"
    >
      <div class="stat">
        <div class="stat-title">
          Current Price
        </div>
        <div class="stat-value text-lg text-primary">
          ${{ formatPrice(currentPrice) }}
        </div>
        <div class="stat-desc">
          Reference (grid starts here)
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          {{ tradingMode === 'LONG' ? `${selectedPeriodLabel} Low (Target)` : `${selectedPeriodLabel} High (Target)` }}
        </div>
        <div
          class="stat-value text-lg"
          :class="tradingMode === 'LONG' ? 'text-error' : 'text-success'"
        >
          ${{ formatPrice(tradingMode === 'LONG' ? periodLow : periodHigh) }}
        </div>
        <div class="stat-desc">
          Grid extends to here
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          Grid Range
        </div>
        <div class="stat-value text-lg">
          ${{ formatPrice(calculatedParams.grid_range) }}
        </div>
        <div class="stat-desc">
          {{ ((calculatedParams.grid_range / currentPrice) * 100).toFixed(2) }}% (with {{ rangeMultiplier }}x buffer)
        </div>
      </div>
    </div>

    <!-- Chart Container -->
    <div
      ref="chartContainer"
      class="chart-container bg-base-200 rounded-lg mb-4"
      :class="{ 'flex items-center justify-center': !hasData }"
    >
      <div
        v-if="!hasData && !isLoading"
        class="text-center text-base-content/50"
      >
        <p class="text-lg">
          Select a symbol and date range to view OHLC data with grid overlay
        </p>
      </div>
    </div>

    <!-- Grid Parameters -->
    <div
      v-if="hasData"
      class="mb-4 p-4 bg-base-200 rounded-lg"
    >
      <h3 class="font-semibold mb-4">
        Grid Parameters
      </h3>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <!-- Total Levels -->
        <div class="form-control w-full sm:w-auto">
          <label class="label">
            <span class="label-text">Total Levels</span>
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="totalLevels"
              type="number"
              min="10"
              max="1000"
              step="1"
              class="input input-bordered input-sm w-20"
            >
            <input
              v-model.number="totalLevels"
              type="range"
              min="10"
              max="500"
              step="10"
              class="range range-sm flex-1"
            >
          </div>
        </div>

        <!-- Profit Ratio -->
        <div class="form-control w-full sm:w-auto">
          <label class="label">
            <span class="label-text">Profit Ratio</span>
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="profitRatio"
              type="number"
              min="1"
              max="100"
              step="0.1"
              class="input input-bordered input-sm w-20"
            >
            <input
              v-model.number="profitRatio"
              type="range"
              min="2"
              max="20"
              step="1"
              class="range range-sm flex-1"
            >
          </div>
        </div>

        <!-- Range Multiplier -->
        <div class="form-control w-full sm:w-auto">
          <label class="label">
            <span class="label-text">Range Multiplier</span>
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="rangeMultiplier"
              type="number"
              min="0.5"
              max="5"
              step="0.1"
              class="input input-bordered input-sm w-20"
            >
            <input
              v-model.number="rangeMultiplier"
              type="range"
              min="1.0"
              max="3.0"
              step="0.1"
              class="range range-sm flex-1"
            >
          </div>
        </div>

        <!-- Capital USD (log scale) -->
        <div class="form-control w-full sm:w-auto">
          <label class="label">
            <span class="label-text">Capital (USD)</span>
          </label>
          <div class="flex items-center gap-2">
            <div class="relative">
              <span class="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-base-content/70">$</span>
              <input
                :value="capitalUSD.toLocaleString()"
                type="text"
                class="input input-bordered input-sm w-32 pl-5"
                @change="updateCapitalFromInput($event.target.value)"
              >
            </div>
            <input
              :value="capitalLogSlider"
              type="range"
              min="1"
              max="7"
              step="0.01"
              class="range range-sm flex-1"
              @input="updateCapitalFromLog($event.target.value)"
            >
          </div>
          <div class="text-xs text-base-content/50 mt-1">
            = {{ maxPositionBase.toFixed(6) }} {{ baseAsset }}
          </div>
        </div>

        <!-- Tick Size -->
        <div class="form-control w-full sm:w-auto">
          <label class="label">
            <span class="label-text">Tick Size ($)</span>
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="tickSize"
              type="number"
              min="0.0001"
              max="100"
              step="0.0001"
              class="input input-bordered input-sm w-24"
            >
            <button
              class="btn btn-sm btn-outline"
              :class="{ 'loading': isFetchingTickSize }"
              :disabled="isFetchingTickSize"
              @click="fetchTickSize"
            >
              Fetch
            </button>
          </div>
          <div class="text-xs text-base-content/50 mt-1">
            Entry: {{ entrySpacingTicks.toFixed(1) }} ticks | Exit: {{ exitSpacingTicks.toFixed(1) }} ticks
          </div>
        </div>
      </div>

      <!-- Sizing Mode Toggle -->
      <div class="divider">
        Sizing Mode
      </div>

      <div class="flex items-center gap-4 mb-4">
        <div class="join flex-wrap">
          <button
            v-for="mode in sizingModes"
            :key="mode.value"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active btn-primary': sizingMode === mode.value }"
            @click="sizingMode = mode.value"
          >
            {{ mode.label }}
          </button>
        </div>
        <span class="text-sm text-base-content/70">
          {{ sizingModeDescription }}
        </span>
      </div>

      <!-- Calculator Parameters (shown when not flat) -->
      <div
        v-if="sizingMode !== 'flat'"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <!-- max_order_size (calculated from multiplier) -->
        <div class="form-control w-full sm:w-auto">
          <label class="label">
            <span class="label-text">max_order_size ({{ maxOrderSizeMultiplier }}x)</span>
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="maxOrderSizeMultiplier"
              type="number"
              min="1"
              max="10"
              step="0.1"
              class="input input-bordered input-sm w-20"
            >
            <input
              v-model.number="maxOrderSizeMultiplier"
              type="range"
              min="1.0"
              max="5.0"
              step="0.1"
              class="range range-sm flex-1"
            >
          </div>
          <div class="text-xs text-base-content/50 mt-1">
            {{ baseOrderSize.toFixed(6) }} → {{ maxOrderSizeCalc.toFixed(6) }} {{ baseAsset }}
          </div>
        </div>

        <!-- Spacing Factor (linear or geometric) -->
        <div class="form-control w-full sm:w-auto">
          <label class="label">
            <span class="label-text">{{ sizingMode === 'linear' ? 'linear_spacing_factor' : 'geometric_spacing_factor' }}</span>
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="spacingFactor"
              type="number"
              min="1"
              max="5"
              step="0.1"
              class="input input-bordered input-sm w-20"
            >
            <input
              v-model.number="spacingFactor"
              type="range"
              min="1.0"
              max="3.0"
              step="0.1"
              class="range range-sm flex-1"
            >
          </div>
          <div class="text-xs text-base-content/50 mt-1">
            Last gap = {{ spacingFactor }}x first gap
          </div>
        </div>

        <!-- geometric_scaling_curve (geometric only) -->
        <div
          v-if="sizingMode === 'geometric'"
          class="form-control w-full sm:w-auto"
        >
          <label class="label">
            <span class="label-text">geometric_scaling_curve</span>
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model.number="geometricScalingCurve"
              type="number"
              min="0.5"
              max="3"
              step="0.1"
              class="input input-bordered input-sm w-20"
            >
            <input
              v-model.number="geometricScalingCurve"
              type="range"
              min="0.5"
              max="3.0"
              step="0.1"
              class="range range-sm flex-1"
            >
          </div>
          <div class="text-xs text-base-content/50 mt-1">
            1.0 = linear, &gt;1 = back-loaded growth
          </div>
        </div>
      </div>

      <!-- Validation Warning -->
      <div
        v-if="validationWarning"
        class="alert alert-warning mt-4"
      >
        <span>{{ validationWarning }}</span>
      </div>

      <!-- Order Size Minimum Warning -->
      <OrderSizeWarning
        v-if="hasData && orderSizeUsd !== null"
        :order-size-usd="orderSizeUsd"
        class="mt-4"
      />
    </div>

    <!-- Calculated Parameters -->
    <div
      v-if="hasData"
      class="mb-4 p-4 bg-base-200 rounded-lg"
    >
      <h3 class="font-semibold mb-4">
        Calculated Parameters
      </h3>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div class="stat bg-base-100 rounded-lg p-3">
          <div class="stat-title text-xs">
            Entry Spacing
          </div>
          <div class="stat-value text-sm">
            ${{ formatPrice(calculatedParams.entry_spacing_dollars) }}
          </div>
        </div>
        <div class="stat bg-base-100 rounded-lg p-3">
          <div class="stat-title text-xs">
            Exit Spacing
          </div>
          <div class="stat-value text-sm">
            ${{ formatPrice(calculatedParams.exit_spacing_dollars) }}
          </div>
        </div>
        <div class="stat bg-base-100 rounded-lg p-3">
          <div class="stat-title text-xs">
            Grid Top
          </div>
          <div class="stat-value text-sm">
            ${{ formatPrice(calculatedParams.grid_top) }}
          </div>
        </div>
        <div class="stat bg-base-100 rounded-lg p-3">
          <div class="stat-title text-xs">
            Grid Bottom
          </div>
          <div class="stat-value text-sm">
            ${{ formatPrice(calculatedParams.grid_bottom) }}
          </div>
        </div>
        <div class="stat bg-base-100 rounded-lg p-3">
          <div class="stat-title text-xs">
            Order Size (min)
          </div>
          <div class="stat-value text-sm">
            {{ calculatedParams.order_size?.toFixed(6) }} {{ baseAsset }}
          </div>
          <div
            class="stat-desc text-xs font-semibold"
            :class="{
              'text-error': orderSizeUsd !== null && orderSizeUsd < 5,
              'text-warning': orderSizeUsd !== null && orderSizeUsd >= 5 && orderSizeUsd < 7,
              'text-success': orderSizeUsd !== null && orderSizeUsd >= 7,
            }"
          >
            ${{ (calculatedParams.order_size * currentPrice).toFixed(2) }}
            <span
              v-if="orderSizeUsd !== null && orderSizeUsd < 5"
              class="ml-1"
            >below $5 min</span>
          </div>
        </div>
        <div class="stat bg-base-100 rounded-lg p-3">
          <div class="stat-title text-xs">
            Total Capital
          </div>
          <div class="stat-value text-sm">
            ${{ totalCapitalUSD.toFixed(2) }}
          </div>
          <div class="stat-desc text-xs">
            {{ totalCapital.toFixed(6) }} {{ baseAsset }}
          </div>
        </div>
      </div>

      <!-- Config Output -->
      <div class="mt-4">
        <div class="text-sm font-semibold mb-2">
          Config Output
        </div>
        <div class="relative">
          <pre class="bg-base-300 p-3 pr-10 rounded-lg text-xs overflow-x-auto"><code>{{ JSON.stringify(configOutput, null, 2) }}</code></pre>
          <button
            class="absolute top-2 right-2 btn btn-ghost btn-xs"
            title="Copy to clipboard"
            @click="copyToClipboard"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4 h-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-2 mt-4">
        <button
          class="btn btn-sm btn-outline"
          @click="resetToDefaults"
        >
          Reset Defaults
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import Plotly from 'plotly.js-dist-min';
import api from '@/api/axios';
import OrderSizeWarning from '@/components/OrderSizeWarning.vue';

// Common symbols (matching OHLC Explorer)
const commonSymbols = [
  'BTC/USDT',
  'ETH/USDT',
  'SOL/USDT',
  'XRP/USDT',
  'BNB/USDT',
  'ADA/USDT',
  'DOGE/USDT',
  'DASH/USDT',
  'AVAX/USDT',
  'DOT/USDT',
  'MATIC/USDT',
  'LINK/USDT',
  'UNI/USDT',
  'ATOM/USDT',
  'LTC/USDT',
  'FIL/USDT',
  'FARTCOIN/USDT',
  'PENGU/USDT',
  'WIF/USDT',
  'TRUMP/USDT',
  'PEPE/USDT',
];

const timeframes = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1h', value: '1h' },
  { label: '4h', value: '4h' },
  { label: '1d', value: '1d' },
];

const sizingModes = [
  { value: 'flat', label: 'Flat' },
  { value: 'linear', label: 'Linear' },
  { value: 'geometric', label: 'Geometric' },
];

// Data selection state
const chartContainer = ref(null);
const symbolInput = ref('BTC/USDT');
const symbol = ref('BTC/USDT');
const showDropdown = ref(false);
const filteredSymbols = ref([...commonSymbols]);
const selectedTimeframe = ref('1h');
const startDateTime = ref('');
const endDateTime = ref('');
const chartData = ref([]);
const isLoading = ref(false);
const error = ref(null);

// Trading mode
const tradingMode = ref('LONG');

// Range lookback period
const rangePeriod = ref('24h');
const rangePeriods = [
  { value: '24h', label: '24h', hours: 24 },
  { value: '48h', label: '48h', hours: 48 },
  { value: '72h', label: '72h', hours: 72 },
  { value: '1w', label: '1W', hours: 168 },
  { value: '1m', label: '1M', hours: 720 },
  { value: '1y', label: '1Y', hours: 8760 },
];

// Grid parameters
const totalLevels = ref(200);
const profitRatio = ref(10);
const rangeMultiplier = ref(1.2);

// Capital-based sizing
const capitalUSD = ref(1000);  // Account capital in USD
const tickSize = ref(0.10);  // Tick size in dollars (e.g., $0.10 for BTC)
const isFetchingTickSize = ref(false);

// Log scale slider for capital (10^1 to 10^7 = $10 to $10M)
const capitalLogSlider = computed(() => {
  return Math.log10(capitalUSD.value);
});

const updateCapitalFromLog = (logValue) => {
  capitalUSD.value = Math.round(Math.pow(10, parseFloat(logValue)));
};

const updateCapitalFromInput = (value) => {
  // Remove $ and commas, then parse as number
  const parsed = parseFloat(value.replace(/[$,]/g, ''));
  if (!isNaN(parsed) && parsed > 0) {
    capitalUSD.value = parsed;
  }
};

// Sizing parameters (matching Grid V3 config names)
const sizingMode = ref('flat');
const maxOrderSizeMultiplier = ref(2.0);  // max_order_size / order_size ratio
const spacingFactor = ref(2.0);  // linear_spacing_factor or geometric_spacing_factor
const geometricScalingCurve = ref(1.0);  // geometric_scaling_curve (1.0 = linear, >1 = back-loaded)

// Computed properties
const hasData = computed(() => chartData.value.length > 0);

const baseAsset = computed(() => {
  return symbol.value.split('/')[0] || 'BTC';
});

const sizingModeDescription = computed(() => {
  switch (sizingMode.value) {
    case 'flat':
      return 'All orders have the same size';
    case 'linear':
      return 'Order sizes grow linearly from min to max';
    case 'geometric':
      return 'Order sizes grow with configurable curve';
    default:
      return '';
  }
});

// Range detection from loaded data based on selected period
const selectedPeriodHours = computed(() => {
  const period = rangePeriods.find(p => p.value === rangePeriod.value);
  return period ? period.hours : 24;
});

const selectedPeriodLabel = computed(() => {
  const period = rangePeriods.find(p => p.value === rangePeriod.value);
  return period ? period.label : '24h';
});

const barsInPeriod = computed(() => {
  const barsPerHour = {
    '1m': 60,
    '5m': 12,
    '15m': 4,
    '1h': 1,
    '4h': 0.25,
    '1d': 1/24,
  }[selectedTimeframe.value] || 1;
  return Math.ceil(selectedPeriodHours.value * barsPerHour);
});

const periodHigh = computed(() => {
  if (!hasData.value) return 0;
  const recent = chartData.value.slice(-barsInPeriod.value);
  return Math.max(...recent.map(c => c.high));
});

const periodLow = computed(() => {
  if (!hasData.value) return 0;
  const recent = chartData.value.slice(-barsInPeriod.value);
  return Math.min(...recent.map(c => c.low));
});

// Current price (latest close)
const currentPrice = computed(() => {
  if (!hasData.value) return 0;
  return chartData.value[chartData.value.length - 1].close;
});

// Max position derived from capital
const maxPositionBase = computed(() => {
  if (currentPrice.value === 0) return 0;
  return capitalUSD.value / currentPrice.value;
});

// Base order size (order_size in Grid V3 - min size for linear/geometric)
const baseOrderSize = computed(() => {
  if (sizingMode.value === 'flat') {
    return maxPositionBase.value / totalLevels.value;
  }
  // For linear/geometric: calculate based on max_order_size multiplier
  // Sum of arithmetic series: n * (min + max) / 2 = total
  // With multiplier: max = min * multiplier
  // n * min * (1 + multiplier) / 2 = total
  // min = 2 * total / (n * (1 + multiplier))
  const n = totalLevels.value;
  const multiplier = maxOrderSizeMultiplier.value;
  return (2 * maxPositionBase.value) / (n * (1 + multiplier));
});

// Max order size (max_order_size in Grid V3)
const maxOrderSizeCalc = computed(() => {
  return baseOrderSize.value * maxOrderSizeMultiplier.value;
});

// Spacing in ticks
const entrySpacingTicks = computed(() => {
  if (tickSize.value <= 0) return 0;
  return calculatedParams.value.entry_spacing_dollars / tickSize.value;
});

const exitSpacingTicks = computed(() => {
  if (tickSize.value <= 0) return 0;
  return calculatedParams.value.exit_spacing_dollars / tickSize.value;
});

// Grid calculation
const calculatedParams = computed(() => {
  if (!hasData.value) {
    return {
      reference_price: 0,
      entry_spacing_dollars: 0,
      exit_spacing_dollars: 0,
      grid_top: 0,
      grid_bottom: 0,
      order_size: 0,
      entry_levels: [],
      order_sizes: [],
    };
  }

  // Reference price is current price (grid starts from here)
  const reference_price = currentPrice.value;

  // Grid boundaries based on trading mode
  // LONG: grid from current price DOWN to period low
  // SHORT: grid from current price UP to period high
  let grid_top, grid_bottom, grid_range;

  if (tradingMode.value === 'LONG') {
    grid_top = reference_price;
    // Apply range multiplier to extend beyond period low (safety buffer)
    const extended_low = reference_price - (reference_price - periodLow.value) * rangeMultiplier.value;
    grid_bottom = extended_low;
    grid_range = grid_top - grid_bottom;
  } else {
    grid_bottom = reference_price;
    // Apply range multiplier to extend beyond period high (safety buffer)
    const extended_high = reference_price + (periodHigh.value - reference_price) * rangeMultiplier.value;
    grid_top = extended_high;
    grid_range = grid_top - grid_bottom;
  }

  // Base spacing from the actual grid range
  const base_spacing = grid_range / totalLevels.value;

  // Calculate entry levels with spacing
  const entry_levels = [];
  const entry_spacings = [];

  if (sizingMode.value === 'flat') {
    for (let i = 0; i < totalLevels.value; i++) {
      entry_spacings.push(base_spacing);
    }
  } else if (sizingMode.value === 'linear') {
    // Normalize so total distance equals grid_range
    const growth_per_level = (spacingFactor.value - 1.0) / Math.max(totalLevels.value - 1, 1);
    let total_factor = 0;
    for (let i = 0; i < totalLevels.value; i++) {
      total_factor += (1.0 + growth_per_level * i);
    }
    const normalized_base = grid_range / total_factor;
    for (let i = 0; i < totalLevels.value; i++) {
      entry_spacings.push(normalized_base * (1.0 + growth_per_level * i));
    }
  } else if (sizingMode.value === 'geometric') {
    // Normalize so total distance equals grid_range
    const per_level_factor = Math.pow(spacingFactor.value, 1.0 / Math.max(totalLevels.value - 1, 1));
    let total_factor = 0;
    for (let i = 0; i < totalLevels.value; i++) {
      total_factor += Math.pow(per_level_factor, i);
    }
    const normalized_base = grid_range / total_factor;
    for (let i = 0; i < totalLevels.value; i++) {
      entry_spacings.push(normalized_base * Math.pow(per_level_factor, i));
    }
  }

  // Generate entry levels from spacings
  let cumulative_distance = 0;
  for (let i = 0; i < totalLevels.value; i++) {
    cumulative_distance += entry_spacings[i];
    if (tradingMode.value === 'LONG') {
      entry_levels.push(reference_price - cumulative_distance);
    } else {
      entry_levels.push(reference_price + cumulative_distance);
    }
  }

  // Calculate order sizes
  const order_sizes = calculateOrderSizes();

  // Exit spacing (based on average entry spacing)
  const avg_entry_spacing = grid_range / totalLevels.value;
  const exit_spacing = avg_entry_spacing * profitRatio.value;

  // Order size (base/min for display)
  const order_size = baseOrderSize.value;

  return {
    reference_price,
    entry_spacing_dollars: avg_entry_spacing,
    exit_spacing_dollars: exit_spacing,
    grid_top,
    grid_bottom,
    order_size,
    entry_levels,
    order_sizes,
    grid_range,
  };
});

// Calculate order sizes based on sizing mode (matching Grid V3 calculators)
function calculateOrderSizes() {
  const sizes = [];
  const n = totalLevels.value;
  const min_size = baseOrderSize.value;
  const max_size = maxOrderSizeCalc.value;

  if (sizingMode.value === 'flat') {
    for (let i = 0; i < n; i++) {
      sizes.push(min_size);
    }
  } else {
    for (let i = 0; i < n; i++) {
      const level_ratio = i / n;

      if (sizingMode.value === 'linear') {
        // Linear: straight interpolation (geometric_scaling_curve = 1.0)
        const size = min_size + (max_size - min_size) * level_ratio;
        sizes.push(size);
      } else if (sizingMode.value === 'geometric') {
        // Geometric: curved interpolation using geometric_scaling_curve
        const curved_ratio = Math.pow(level_ratio, geometricScalingCurve.value);
        const size = min_size + (max_size - min_size) * curved_ratio;
        sizes.push(size);
      }
    }
  }

  return sizes;
}

// Total capital used
const totalCapital = computed(() => {
  return calculatedParams.value.order_sizes.reduce((sum, size) => sum + size, 0);
});

// Total capital in USD
const totalCapitalUSD = computed(() => {
  return totalCapital.value * currentPrice.value;
});

// Validation warning
const validationWarning = computed(() => {
  const warnings = [];

  // Check if entry spacing is less than tick size
  if (calculatedParams.value.entry_spacing_dollars < tickSize.value) {
    warnings.push(`Entry spacing ($${calculatedParams.value.entry_spacing_dollars.toFixed(4)}) is less than tick size ($${tickSize.value}). Consider reducing levels or increasing range.`);
  }

  // Check if total capital significantly exceeds the input capital (due to rounding)
  const capitalDiff = Math.abs(totalCapitalUSD.value - capitalUSD.value);
  if (capitalDiff > capitalUSD.value * 0.01) {  // More than 1% difference
    warnings.push(`Calculated total ($${totalCapitalUSD.value.toFixed(2)}) differs from input capital. This is due to sizing approximation.`);
  }

  return warnings.length > 0 ? warnings.join(' | ') : null;
});

// Order size in USD for minimum order validation
const orderSizeUsd = computed(() => {
  if (!hasData.value || currentPrice.value === 0) return null;
  return baseOrderSize.value * currentPrice.value;
});

// Format price for display
const formatPrice = (price) => {
  if (typeof price !== 'number' || isNaN(price)) return '0.00';
  if (price >= 1000) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(price);
};

// Symbol filtering
const filterSymbols = () => {
  const query = symbolInput.value.toUpperCase();
  filteredSymbols.value = commonSymbols.filter(s => s.includes(query));
  showDropdown.value = true;
};

const selectSymbol = (sym) => {
  symbolInput.value = sym;
  symbol.value = sym;
  showDropdown.value = false;
};

// Fetch OHLC data
const fetchOHLCData = async () => {
  // Update symbol from input (in case user typed without pressing Enter)
  if (symbolInput.value && symbolInput.value !== symbol.value) {
    symbol.value = symbolInput.value.toUpperCase().trim();
  }

  if (!symbol.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.get('/api/market/ohlcv/', {
      params: {
        symbol: symbol.value,
        timeframe: selectedTimeframe.value,
        start_date: startDateTime.value ? new Date(startDateTime.value + 'Z').toISOString() : '',
        end_date: endDateTime.value ? new Date(endDateTime.value + 'Z').toISOString() : '',
      },
    });

    if (response.data && Array.isArray(response.data)) {
      chartData.value = response.data;
      // Wait for Vue reactivity to update computed properties before rendering
      await nextTick();
      renderChart();
    } else {
      error.value = 'No data returned from API';
    }
  } catch (err) {
    console.error('Error fetching OHLC data:', err);
    error.value = err.response?.data?.message || err.message || 'Failed to fetch data';
  } finally {
    isLoading.value = false;
  }
};

// Fetch tick size directly from Bybit public API
const fetchTickSize = async () => {
  if (!symbol.value) return;

  isFetchingTickSize.value = true;
  error.value = null;

  try {
    // Convert symbol format: BTC/USDT -> BTCUSDT
    const bybitSymbol = symbol.value.replace('/', '');

    // Try linear (perpetual futures) first, then spot
    const categories = ['linear', 'spot'];
    let foundTickSize = null;

    for (const category of categories) {
      const response = await fetch(
        `https://api.bybit.com/v5/market/instruments-info?category=${category}&symbol=${bybitSymbol}`
      );

      if (!response.ok) continue;

      const data = await response.json();

      if (data.retCode == 0 && data.result?.list?.length > 0) {
        const instrument = data.result.list[0];
        const fetchedTickSize = instrument.priceFilter?.tickSize;

        if (fetchedTickSize) {
          foundTickSize = parseFloat(fetchedTickSize);
          console.log(`Found tick size ${foundTickSize} on Bybit ${category}`);
          break;
        }
      }
    }

    if (foundTickSize !== null) {
      tickSize.value = foundTickSize;
    } else {
      error.value = `Symbol ${bybitSymbol} not found on Bybit`;
    }
  } catch (err) {
    console.error('Failed to fetch tick size:', err);
    error.value = `Failed to fetch tick size: ${err.message}`;
  } finally {
    isFetchingTickSize.value = false;
  }
};

// Calculate Y-axis range to include both OHLC data and grid levels
const calculateYAxisRange = () => {
  const params = calculatedParams.value;

  // Get OHLC data range
  const ohlcHighs = chartData.value.map(c => c.high);
  const ohlcLows = chartData.value.map(c => c.low);
  const dataHigh = Math.max(...ohlcHighs);
  const dataLow = Math.min(...ohlcLows);

  // Get grid range
  const gridHigh = params.grid_top || dataHigh;
  const gridLow = params.grid_bottom || dataLow;

  // Include reference price
  const refPrice = params.reference_price || currentPrice.value;

  // Calculate overall range (include both data and grid)
  const overallHigh = Math.max(dataHigh, gridHigh, refPrice);
  const overallLow = Math.min(dataLow, gridLow, refPrice);

  // Add 5% padding
  const range = overallHigh - overallLow;
  const padding = range * 0.05;

  return [overallLow - padding, overallHigh + padding];
};

// Render chart with grid overlay
const renderChart = () => {
  if (!chartContainer.value || !hasData.value) return;

  const dates = chartData.value.map(c => new Date(c.timestamp));
  const opens = chartData.value.map(c => c.open);
  const highs = chartData.value.map(c => c.high);
  const lows = chartData.value.map(c => c.low);
  const closes = chartData.value.map(c => c.close);

  // Main candlestick trace
  const traces = [
    {
      x: dates,
      open: opens,
      high: highs,
      low: lows,
      close: closes,
      type: 'candlestick',
      increasing: { line: { color: '#26a69a' }, fillcolor: '#26a69a' },
      decreasing: { line: { color: '#ef5350' }, fillcolor: '#ef5350' },
      name: symbol.value,
      xaxis: 'x',
      yaxis: 'y',
    },
  ];

  // Add horizontal bars for order sizes
  const params = calculatedParams.value;
  if (params.entry_levels.length > 0 && params.order_sizes.length > 0) {
    // Sample levels for display (show max 50 to avoid clutter)
    const step = Math.max(1, Math.floor(params.entry_levels.length / 50));
    const sampledLevels = [];
    const sampledSizes = [];

    for (let i = 0; i < params.entry_levels.length; i += step) {
      sampledLevels.push(params.entry_levels[i]);
      sampledSizes.push(params.order_sizes[i]);
    }

    // Horizontal bar trace for order sizes
    traces.push({
      x: sampledSizes,
      y: sampledLevels,
      type: 'bar',
      orientation: 'h',
      xaxis: 'x2',
      yaxis: 'y',
      marker: {
        color: tradingMode.value === 'LONG' ? '#3b82f6' : '#f97316',
        opacity: 0.7,
      },
      hovertemplate: '%{x:.4f} ' + baseAsset.value + ' @ $%{y:,.2f}<extra></extra>',
      showlegend: false,
      name: 'Order Size',
    });
  }

  // Build shapes for grid lines
  const shapes = [];

  // Reference price line (thick)
  shapes.push({
    type: 'line',
    xref: 'paper',
    x0: 0,
    x1: 0.85,
    yref: 'y',
    y0: params.reference_price,
    y1: params.reference_price,
    line: {
      color: tradingMode.value === 'LONG' ? '#22c55e' : '#ef4444',
      width: 3,
      dash: 'solid',
    },
  });

  // Grid bottom/top line (dashed)
  const boundaryPrice = tradingMode.value === 'LONG' ? params.grid_bottom : params.grid_top;
  shapes.push({
    type: 'line',
    xref: 'paper',
    x0: 0,
    x1: 0.85,
    yref: 'y',
    y0: boundaryPrice,
    y1: boundaryPrice,
    line: {
      color: '#6b7280',
      width: 2,
      dash: 'dash',
    },
  });

  // Entry level lines (sample to avoid too many)
  const levelStep = Math.max(1, Math.floor(params.entry_levels.length / 30));
  for (let i = 0; i < params.entry_levels.length; i += levelStep) {
    shapes.push({
      type: 'line',
      xref: 'paper',
      x0: 0,
      x1: 0.85,
      yref: 'y',
      y0: params.entry_levels[i],
      y1: params.entry_levels[i],
      line: {
        color: tradingMode.value === 'LONG' ? '#3b82f6' : '#f97316',
        width: 1,
        dash: 'solid',
      },
      opacity: 0.5,
    });
  }

  // Period range shading
  shapes.push({
    type: 'rect',
    xref: 'paper',
    x0: 0,
    x1: 0.85,
    yref: 'y',
    y0: periodLow.value,
    y1: periodHigh.value,
    fillcolor: tradingMode.value === 'LONG' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
    line: { width: 0 },
  });

  const layout = {
    title: {
      text: `${symbol.value} - Range Explorer (${tradingMode.value})`,
      font: { size: 16 },
    },
    xaxis: {
      title: 'Date',
      rangeslider: { visible: false },
      type: 'date',
      domain: [0, 0.85],
    },
    xaxis2: {
      title: 'Size',
      domain: [0.88, 1],
      anchor: 'y',
      side: 'top',
    },
    yaxis: {
      title: 'Price',
      side: 'left',
      autorange: false,
      fixedrange: false,
      showgrid: true,
      gridcolor: 'rgba(107, 114, 128, 0.2)',
      range: calculateYAxisRange(),
    },
    shapes: shapes,
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: '#9ca3af',
    },
    margin: { t: 50, b: 50, l: 70, r: 30 },
    showlegend: false,
    annotations: [
      {
        x: 0.92,
        y: params.reference_price,
        xref: 'paper',
        yref: 'y',
        text: 'CURRENT',
        showarrow: false,
        font: { size: 10, color: tradingMode.value === 'LONG' ? '#22c55e' : '#ef4444' },
      },
    ],
  };

  const config = {
    responsive: true,
    displayModeBar: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
  };

  Plotly.react(chartContainer.value, traces, layout, config);
};

// Generate config object for display and copy (matching Grid V3 actual calculator code)
const configOutput = computed(() => {
  const config = {
    calculator: sizingMode.value,
    entry_spacing_dollars: parseFloat(calculatedParams.value.entry_spacing_dollars.toFixed(8)),
    exit_spacing_dollars: parseFloat(calculatedParams.value.exit_spacing_dollars.toFixed(8)),
    order_size: parseFloat(baseOrderSize.value.toFixed(8)),
    position_max_size: parseFloat(maxPositionBase.value.toFixed(8)),
  };

  if (sizingMode.value === 'linear') {
    config.linear_spacing_factor = spacingFactor.value;
    config.max_order_size = parseFloat(maxOrderSizeCalc.value.toFixed(8));
  } else if (sizingMode.value === 'geometric') {
    config.geometric_spacing_factor = spacingFactor.value;
    config.geometric_scaling_curve = geometricScalingCurve.value;
    config.max_order_size = parseFloat(maxOrderSizeCalc.value.toFixed(8));
  }

  return config;
});

// Copy config to clipboard
const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(JSON.stringify(configOutput.value, null, 2));
    alert('Config copied to clipboard!');
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};

// Reset to defaults
const resetToDefaults = () => {
  totalLevels.value = 200;
  profitRatio.value = 10;
  rangeMultiplier.value = 1.2;
  capitalUSD.value = 1000;
  tickSize.value = 0.10;
  sizingMode.value = 'flat';
  maxOrderSizeMultiplier.value = 2.0;
  spacingFactor.value = 2.0;
  geometricScalingCurve.value = 1.0;
};

// Event handlers
const handleClickOutside = (event) => {
  if (!event.target.closest('.dropdown') && !event.target.closest('.relative')) {
    showDropdown.value = false;
  }
};

const handleResize = () => {
  if (hasData.value && chartContainer.value) {
    Plotly.Plots.resize(chartContainer.value);
  }
};

// Lifecycle
onMounted(() => {
  // Set default dates (last 7 days)
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);

  // Format as datetime-local value (YYYY-MM-DDTHH:MM) in GMT
  const formatDateTimeLocal = (date) => {
    return date.toISOString().slice(0, 16);
  };
  endDateTime.value = formatDateTimeLocal(end);
  startDateTime.value = formatDateTimeLocal(start);

  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
  if (chartContainer.value) {
    Plotly.purge(chartContainer.value);
  }
});

// Watchers - re-render chart when parameters change
watch([tradingMode, totalLevels, profitRatio, rangeMultiplier, capitalUSD, tickSize, sizingMode, maxOrderSizeMultiplier, spacingFactor, geometricScalingCurve, rangePeriod], () => {
  if (hasData.value) {
    renderChart();
  }
});

watch(selectedTimeframe, async () => {
  if (hasData.value) {
    await fetchOHLCData();
  }
});
</script>

<style scoped>
.range-explorer-tab {
  width: 100%;
}

.chart-container {
  width: 100%;
  height: 500px;
  min-height: 400px;
}

@media (max-width: 639px) {
  .chart-container {
    height: 360px;
    min-height: 320px;
  }
  .chart-panel-large {
    height: 360px;
    min-height: 320px;
  }
  .chart-panel,
  .analytics-chart {
    height: 280px;
    min-height: 240px;
  }
}
</style>
